import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Plus,
  Edit,
  Trash2,
  Database,
  Calendar,
  Clock,
  MapPin,
  PlaneTakeoff,
  PlaneLanding,
  Search,
  Filter,
  X,
  Plane,
  User,
  AlertTriangle,
  ChevronRight,
  Eye,
  ChevronDown,
  Layers,
  Users,
  RefreshCw,
  ArrowLeftRight,
  CheckSquare
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import TripDeleteModal from '../components/TripDeleteModal';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useDialog } from '../contexts/DialogContext';

// Helper functions
const getAuthHeaders = (navigate) => {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  if (!token) {
    toast.error('Authentication required. Please login again.');
    navigate('/login');
    return null;
  }
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

const handleAuthError = (error, navigate) => {
  if (error.response?.status === 401) {
    toast.error('Session expired. Please login again.');
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    navigate('/login');
    return true;
  }
  return false;
};

const formatDate = (dateValue) => {
  if (!dateValue) return 'N/A';
  const dateStr = typeof dateValue === 'string' ? dateValue : dateValue.toISOString();
  const [year, month, day] = dateStr.substring(0, 10).split('-').map(Number);
  if (!year || !month || !day) return 'N/A';
  return format(new Date(year, month - 1, day), 'MMM dd, yyyy');
};

const formatDateMobile = (dateValue) => {
  if (!dateValue) return 'N/A';
  const dateStr = typeof dateValue === 'string' ? dateValue : dateValue.toISOString();
  const [year, month, day] = dateStr.substring(0, 10).split('-').map(Number);
  if (!year || !month || !day) return 'N/A';
  return format(new Date(year, month - 1, day), 'MMM dd');
};

const formatTime = (time) => {
  if (time === null || time === undefined) return '---';
  const timeStr = time.toString().padStart(4, '0');
  return `${timeStr.slice(0, 2)}:${timeStr.slice(2)}`;
};

const formatNumber = (value, decimalPlaces = 1) => {
  if (value === null || value === undefined || isNaN(value)) return '---';
  const numericValue = parseFloat(value);
  if (isNaN(numericValue) || numericValue === 0) return '---';
  return numericValue.toFixed(decimalPlaces);
};

// Prefer the canonical type_code resolved via aircraft -> aircraft_types
// (aircraft_type_lookup, joined server-side off aircraft_type_id). Falls back
// to the free-text flight_logs.aircraft_type column for flights whose tail
// number doesn't have an aircraft record yet (e.g. older imported data).
const getAircraftType = (flight) => flight.aircraft_type_lookup || flight.aircraft_type || '';


const getFlightStatus = (flight) => {
  const hasOutTime = flight.actual_out || flight.scheduled_out;
  const hasInTime = flight.actual_in || flight.scheduled_in;
  const hasTotalTime = flight.total_time && flight.total_time > 0;
  const hasTailNumber = flight.aircraft_id;
  const completedFields = [hasOutTime, hasInTime, hasTotalTime].filter(Boolean).length;
  
  const flightDate = new Date(flight.date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const isFuture = flightDate > today;
  const isPast = flightDate < today;
  const isToday = flightDate.getTime() === today.getTime();
  
  // Check if flight is fully completed first
  if (completedFields === 3 && hasTotalTime) {
    return { status: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800' };
  }
  
  // Past flights that aren't completed
  if (isPast && completedFields < 3) {
    if (!hasTailNumber) {
      return { status: 'past', label: 'Past Not Completed', color: 'bg-yellow-100 text-orange-800' };
    } else {
      return { status: 'incomplete', label: 'Incomplete', color: 'bg-red-100 text-red-800' };
    }
  }
  
  // Future flights (planned)
  if (isFuture) {
    return { status: 'planned', label: 'Planned', color: 'bg-blue-100 text-blue-700' };
  }
  
  // Today's flights or flights with some progress
  if ((isToday || completedFields > 0) && hasTailNumber) {
    return { status: 'in-progress', label: 'In Progress', color: 'bg-yellow-100 text-yellow-800' };
  }
  
  // Default fallback
  return { status: 'planned', label: 'Planned', color: 'bg-blue-100 text-blue-700' };
};

// Mobile Flight Card Component
const MobileFlightCard = React.forwardRef(({ flight, onView, onEdit, onReturnLeg, onDelete, selectable, selected, onToggleSelect }, ref) => {
  const flightStatus = getFlightStatus(flight);

  // Enhanced styling based on flight status
  const getCardStyling = () => {
    const baseClasses = 'shadow-sm';
    switch (flightStatus.status) {
      case 'completed':
        return `${baseClasses} bg-green-50/50 border-green-200`;
      case 'planned':
        return `${baseClasses} bg-blue-50/50 border-blue-200`;
      case 'in-progress':
        return `${baseClasses} bg-yellow-50/50 border-yellow-200 shadow-md`;
      case 'past':
        return `${baseClasses} bg-orange-50/50 border-orange-200 opacity-90`;
      default:
        return `${baseClasses} bg-white border-gray-200`;
    }
  };

  const getBorderColor = () => {
    switch (flightStatus.status) {
      case 'completed': return '#10b981';
      case 'planned': return '#3b82f6';
      case 'in-progress': return '#f59e0b';
      case 'past': return '#f97316';
      default: return 'transparent';
    }
  };

  return (
    <div 
      ref={ref}
      className={`rounded-lg mb-3 overflow-hidden ${getCardStyling()}`}
      style={{
        borderLeft: `4px solid ${getBorderColor()}`
      }}
    >
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            {selectable && (
              <input
                type="checkbox"
                checked={selected}
                onChange={() => onToggleSelect(flight.id)}
                className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                aria-label={`Select flight ${flight.flight_number || ''}`}
              />
            )}
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${flightStatus.color}`}>
              {flightStatus.status === 'in-progress' && <AlertTriangle className="h-3 w-3 mr-1" />}
              {flightStatus.label}
            </span>
            <span className="text-sm font-medium text-gray-900">
              {flight.flight_number || 'N/A'}
            </span>
          </div>
          <div className="text-sm text-gray-500">{formatDateMobile(flight.date)}</div>
        </div>

        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-1">
              <PlaneTakeoff className="h-4 w-4 text-gray-400" />
              <span className="font-medium text-gray-900">{flight.departure_airport}</span>
            </div>
            <div className="text-gray-400">→</div>
            <div className="flex items-center space-x-1">
              <PlaneLanding className="h-4 w-4 text-gray-400" />
              <span className="font-medium text-gray-900">{flight.arrival_airport}</span>
            </div>
          </div>
          <div className="text-sm text-gray-600">{flight.aircraft_id || 'N/A'}</div>
        </div>

        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4">
            <div>
              <span className="text-gray-500">Out:</span>
              <span className="ml-1 font-mono">{formatTime(flight.actual_out || flight.scheduled_out)}</span>
            </div>
            <div>
              <span className="text-gray-500">In:</span>
              <span className="ml-1 font-mono">{formatTime(flight.actual_in || flight.scheduled_in)}</span>
            </div>
          </div>
          <div className="text-right">
            {flight.trip_number || flight.trip_display ? (
              <div className="flex items-center text-blue-600">
                <Layers className="h-3 w-3 mr-1" />
                <span className="text-xs">{flight.trip_number || flight.trip_display}</span>
              </div>
            ) : (
              <span className="text-xs text-gray-400">Individual</span>
            )}
          </div>
        </div>

        {/* Flight Time and Actions */}
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div>
                <span className="text-xs text-gray-500">Total</span>
                <div className="font-semibold text-lg text-gray-900">
                  {formatNumber(flight.total_time)}h
                </div>
              </div>
              <div>
                <span className="text-xs text-gray-500">PIC</span>
                <div className="font-medium text-sm text-gray-700">
                  {formatNumber(flight.pic_time)}h
                </div>
              </div>
              <div>
                <span className="text-xs text-gray-500">SIC</span>
                <div className="font-medium text-sm text-gray-700">
                  {formatNumber(flight.sic_time)}h
                </div>
              </div>
            </div>
            
            {/* Action Buttons - Desktop Style */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onView(flight.id)}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg active:bg-blue-100"
                aria-label="View Flight"
              >
                <Eye className="h-4 w-4" />
              </button>
              <button
                onClick={() => onEdit(flight.id)}
                className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg active:bg-indigo-100"
                aria-label="Edit Flight"
              >
                <Edit className="h-4 w-4" />
              </button>
              <button
                onClick={() => onReturnLeg(flight.id)}
                className="p-2 text-sky-600 hover:bg-sky-50 rounded-lg active:bg-sky-100"
                aria-label="Log Return Leg"
              >
                <ArrowLeftRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => onDelete(flight.id)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg active:bg-red-100"
                aria-label="Delete Flight"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
          
          {/* Remarks Row */}
          {flight.remarks && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <div className="text-xs text-gray-500 truncate" title={flight.remarks}>
                <span className="font-medium">Remarks:</span> {flight.remarks}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

// Add display name for better debugging
MobileFlightCard.displayName = 'MobileFlightCard';

// Mobile Filter Sheet Component
const MobileFilterSheet = ({ filters, setFilters, onClose, onClear }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 md:hidden">
      <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-lg max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Filters</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Aircraft</label>
            <input
              type="text"
              placeholder="Aircraft ID"
              value={filters.aircraft}
              onChange={(e) => setFilters(prev => ({ ...prev, aircraft: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Trip</label>
            <input
              type="text"
              placeholder="Trip Number"
              value={filters.trip}
              onChange={(e) => setFilters(prev => ({ ...prev, trip: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              <option value="planned">Planned</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex space-x-3">
            <button
              onClick={onClear}
              className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Clear All
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Flight Details Modal Component
const formatDuration = (hours) => {
  if (!hours || isNaN(hours) || hours <= 0) return '0h 0m';
  const numericHours = parseFloat(hours);
  if (isNaN(numericHours)) return '0h 0m';
  const h = Math.floor(numericHours);
  const m = Math.round((numericHours - h) * 60);
  return `${h}h ${m}m`;
};

// Helper function to calculate time difference in hours
const calculateTimeDifference = (startTime, endTime) => {
  if (!startTime || !endTime) return null;
  
  const start = typeof startTime === 'string' ? parseInt(startTime) : startTime;
  const end = typeof endTime === 'string' ? parseInt(endTime) : endTime;
  
  if (isNaN(start) || isNaN(end)) return null;
  
  const startHours = Math.floor(start / 100);
  const startMinutes = start % 100;
  const endHours = Math.floor(end / 100);
  const endMinutes = end % 100;
  
  const startTotalMinutes = startHours * 60 + startMinutes;
  let endTotalMinutes = endHours * 60 + endMinutes;
  
  // Handle day crossover
  if (endTotalMinutes < startTotalMinutes) {
    endTotalMinutes += 24 * 60;
  }
  
  const diffMinutes = endTotalMinutes - startTotalMinutes;
  return diffMinutes / 60; // Return as decimal hours
};

const FlightDetailsModal = ({ flight, isOpen, onClose, onEdit, onDelete }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!isOpen || !flight) return null;

  // Calculate time differences
  const scheduledBlockTime = calculateTimeDifference(flight.scheduled_out, flight.scheduled_in);
  const actualBlockTime = calculateTimeDifference(flight.actual_out, flight.actual_in);
  const blockTimeDiff = actualBlockTime && scheduledBlockTime ? actualBlockTime - scheduledBlockTime : null;
  const flightStatus = getFlightStatus(flight);

  // Format time difference for display (block diff)
  const formatBlockDiff = (diff) => {
    if (!diff || Math.abs(diff) < 0.01) return null;
    const absMinutes = Math.abs(diff * 60);
    const hours = Math.floor(absMinutes / 60);
    const minutes = Math.round(absMinutes % 60);
    const sign = diff >= 0 ? '+' : '-';
    
    if (hours > 0) {
      return `${sign}${hours}h ${minutes}m`;
    } else {
      return `${sign}${minutes}m`;
    }
  };

  const blockDiffFormatted = formatBlockDiff(blockTimeDiff);
  const blockDiffColor = blockTimeDiff === null ? 'text-gray-500' : 
                        blockTimeDiff >= 0 ? 'text-red-600' : 'text-green-600';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-0 md:p-4">
      <div className={`bg-white ${isMobile ? 'w-full h-full' : 'rounded-2xl shadow-2xl max-w-6xl w-full max-h-[95vh]'} overflow-hidden`}>
        {/* Header */}
        <div className={`relative bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 ${isMobile ? 'px-4 py-4' : 'px-8 py-6'}`}>
          <div className="flex items-center justify-between">
            <div className={`flex items-center ${isMobile ? 'space-x-2' : 'space-x-4'}`}>
              <div className={`${isMobile ? 'p-2' : 'p-3'} bg-white/20 backdrop-blur-sm rounded-xl`}>
                <Plane className={`${isMobile ? 'h-6 w-6' : 'h-8 w-8'} text-white`} />
              </div>
              <div>
                <h2 className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold text-white`}>
                  Flight {flight.flight_number || 'N/A'}
                </h2>
                <div className={`flex ${isMobile ? 'flex-col space-y-1' : 'items-center space-x-4'} mt-1`}>
                  <p className="text-blue-100 font-medium text-sm">
                    {formatDate(flight.date)}
                  </p>
                  <div className="flex items-center space-x-2 text-blue-100">
                    <span className="font-semibold text-sm">{flight.departure_airport}</span>
                    <span className="text-xs">→</span>
                    <span className="font-semibold text-sm">{flight.arrival_airport}</span>
                  </div>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-white/20 text-white backdrop-blur-sm ${isMobile ? 'self-start' : ''}`}>
                    {flightStatus.status === 'in-progress' && <AlertTriangle className="h-3 w-3 mr-1" />}
                    {flightStatus.label}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-xl transition-colors text-white"
            >
              <X className={`${isMobile ? 'h-5 w-5' : 'h-6 w-6'}`} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={`overflow-y-auto ${isMobile ? 'h-[calc(100vh-180px)]' : 'max-h-[calc(95vh-200px)]'}`}>
          <div className={`${isMobile ? 'p-4' : 'p-8'}`}>
            {/* Key Metrics Cards */}
            <div className={`grid ${isMobile ? 'grid-cols-2' : 'grid-cols-1 md:grid-cols-4'} gap-3 md:gap-4 mb-6 md:mb-8`}>
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-3 md:p-4">
                <div className={`flex items-center ${isMobile ? 'space-x-2' : 'space-x-3'}`}>
                  <div className="p-1.5 md:p-2 bg-green-100 rounded-lg">
                    <Clock className="h-4 w-4 md:h-5 md:w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-green-600 uppercase tracking-wide">Total</p>
                    <p className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-green-900`}>{formatNumber(flight.total_time)}h</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-3 md:p-4">
                <div className={`flex items-center ${isMobile ? 'space-x-2' : 'space-x-3'}`}>
                  <div className="p-1.5 md:p-2 bg-blue-100 rounded-lg">
                    <User className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide">PIC</p>
                    <p className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-blue-900`}>{formatNumber(flight.pic_time)}h</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-200 rounded-xl p-3 md:p-4">
                <div className={`flex items-center ${isMobile ? 'space-x-2' : 'space-x-3'}`}>
                  <div className="p-1.5 md:p-2 bg-purple-100 rounded-lg">
                    <Users className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-purple-600 uppercase tracking-wide">SIC</p>
                    <p className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-purple-900`}>{formatNumber(flight.sic_time)}h</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-3 md:p-4">
                <div className={`flex items-center ${isMobile ? 'space-x-2' : 'space-x-3'}`}>
                  <div className="p-1.5 md:p-2 bg-amber-100 rounded-lg">
                    <Plane className="h-4 w-4 md:h-5 md:w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide">Aircraft</p>
                    <p className={`${isMobile ? 'text-sm' : 'text-lg'} font-bold text-amber-900`}>{flight.aircraft_id || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className={`${isMobile ? 'space-y-6' : 'grid grid-cols-1 lg:grid-cols-3 gap-8'}`}>
              
              {/* Flight Information */}
              <div className="bg-gray-50 rounded-xl p-4 md:p-6">
                <h3 className="text-base md:text-lg font-bold text-gray-900 mb-3 md:mb-4 flex items-center">
                  <div className="p-1.5 bg-gray-200 rounded-lg mr-2 md:mr-3">
                    <Plane className="h-3 w-3 md:h-4 md:w-4 text-gray-600" />
                  </div>
                  Flight Information
                </h3>
                <div className="space-y-3 md:space-y-4">
                  <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-2 gap-4'}`}>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</label>
                      <p className="text-gray-900 font-semibold mt-1 text-sm">{formatDate(flight.date)}</p>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Flight Number</label>
                      <p className="text-gray-900 font-semibold mt-1 text-sm">{flight.flight_number || 'N/A'}</p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Aircraft</label>
                    <div className="mt-1">
                      <p className="text-gray-900 font-semibold text-sm">
                        {flight.aircraft_id || 'N/A'}
                      </p>
                      {getAircraftType(flight) && (
                        <p className="text-xs text-gray-600 font-medium">({getAircraftType(flight)})</p>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Route</label>
                    <div className={`mt-1 flex items-center ${isMobile ? 'space-x-2' : 'space-x-3'}`}>
                      <div className="flex items-center space-x-1">
                        <PlaneTakeoff className="h-3 w-3 md:h-4 md:w-4 text-gray-400" />
                        <span className="font-semibold text-gray-900 text-sm">{flight.departure_airport}</span>
                      </div>
                      <span className="text-gray-400 text-sm">→</span>
                      <div className="flex items-center space-x-1">
                        <PlaneLanding className="h-3 w-3 md:h-4 md:w-4 text-gray-400" />
                        <span className="font-semibold text-gray-900 text-sm">{flight.arrival_airport}</span>
                      </div>
                    </div>
                  </div>
                  
                  {flight.trip_number && (
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Trip</label>
                      <div className="mt-1 flex items-center space-x-2">
                        <Layers className="h-3 w-3 md:h-4 md:w-4 text-blue-500" />
                        <p className="text-gray-900 font-semibold text-sm">{flight.trip_number}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Times Section */}
              <div className="bg-blue-50 rounded-xl p-4 md:p-6">
                <h3 className="text-base md:text-lg font-bold text-gray-900 mb-3 md:mb-4 flex items-center">
                  <div className="p-1.5 bg-blue-200 rounded-lg mr-2 md:mr-3">
                    <Clock className="h-3 w-3 md:h-4 md:w-4 text-blue-600" />
                  </div>
                  Flight Times
                </h3>
                <div className="space-y-3 md:space-y-4">
                  <div className="bg-white rounded-lg p-3 md:p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 md:mb-3">Scheduled</h4>
                    <div className="grid grid-cols-2 gap-2 md:gap-3 text-sm">
                      <div>
                        <label className="text-xs font-medium text-gray-500">Out</label>
                        <p className="font-mono font-semibold text-gray-900 text-sm">{formatTime(flight.scheduled_out)}</p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500">In</label>
                        <p className="font-mono font-semibold text-gray-900 text-sm">{formatTime(flight.scheduled_in)}</p>
                      </div>
                    </div>
                    {scheduledBlockTime && (
                      <div className="mt-2 md:mt-3 pt-2 md:pt-3 border-t border-gray-100">
                        <label className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Scheduled Block</label>
                        <p className="font-semibold text-blue-900 text-sm">{formatDuration(scheduledBlockTime)}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="bg-white rounded-lg p-3 md:p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 md:mb-3">Actual</h4>
                    <div className="grid grid-cols-2 gap-2 md:gap-3 text-sm">
                      <div>
                        <label className="text-xs font-medium text-gray-500">Out</label>
                        <p className="font-mono font-semibold text-gray-900 text-sm">{formatTime(flight.actual_out)}</p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500">Off</label>
                        <p className="font-mono font-semibold text-gray-900 text-sm">{formatTime(flight.actual_off)}</p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500">On</label>
                        <p className="font-mono font-semibold text-gray-900 text-sm">{formatTime(flight.actual_on)}</p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500">In</label>
                        <p className="font-mono font-semibold text-gray-900 text-sm">{formatTime(flight.actual_in)}</p>
                      </div>
                    </div>
                    {actualBlockTime && (
                      <div className="mt-2 md:mt-3 pt-2 md:pt-3 border-t border-gray-100">
                        <label className="text-xs font-semibold text-green-600 uppercase tracking-wide">Actual Block</label>
                        <p className="font-semibold text-green-900 text-sm">{formatDuration(actualBlockTime)}</p>
                      </div>
                    )}
                  </div>

                  {/* Block Difference */}
                  {blockDiffFormatted && scheduledBlockTime && actualBlockTime && (
                    <div className="bg-white rounded-lg p-3 md:p-4 border-2 border-dashed border-gray-200">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Block Difference</label>
                        <div className="flex items-center space-x-1">
                          {blockTimeDiff >= 0 ? (
                            <AlertTriangle className="h-3 w-3 text-red-500" />
                          ) : (
                            <Clock className="h-3 w-3 text-green-500" />
                          )}
                          <p className={`font-bold text-sm ${blockDiffColor}`}>
                            {blockDiffFormatted}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {blockTimeDiff >= 0 ? 'Flight ran longer than scheduled' : 'Flight completed early'}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Flight Hours and Crew */}
              <div className="space-y-4 md:space-y-6">
                {/* Flight Hours */}
                <div className="bg-green-50 rounded-xl p-4 md:p-6">
                  <h3 className="text-base md:text-lg font-bold text-gray-900 mb-3 md:mb-4 flex items-center">
                    <div className="p-1.5 bg-green-200 rounded-lg mr-2 md:mr-3">
                      <Clock className="h-3 w-3 md:h-4 md:w-4 text-green-600" />
                    </div>
                    Flight Hours
                  </h3>
                  <div className="space-y-3">
                    <div className="bg-white rounded-lg p-3 md:p-4">
                      <label className="text-xs font-semibold text-green-600 uppercase tracking-wide">Total Time</label>
                      <p className="text-xl md:text-2xl font-bold text-green-900">{formatNumber(flight.total_time)} hours</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 md:gap-3">
                      <div className="bg-white rounded-lg p-2 md:p-3">
                        <label className="text-xs font-medium text-gray-500">PIC Time</label>
                        <p className="text-sm md:text-lg font-semibold text-gray-900">{formatNumber(flight.pic_time)}h</p>
                      </div>
                      <div className="bg-white rounded-lg p-2 md:p-3">
                        <label className="text-xs font-medium text-gray-500">SIC Time</label>
                        <p className="text-sm md:text-lg font-semibold text-gray-900">{formatNumber(flight.sic_time)}h</p>
                      </div>
                      <div className="bg-white rounded-lg p-2 md:p-3">
                        <label className="text-xs font-medium text-gray-500">Night Time</label>
                        <p className="text-sm md:text-lg font-semibold text-gray-900">{formatNumber(flight.night_time)}h</p>
                      </div>
                      <div className="bg-white rounded-lg p-2 md:p-3">
                        <label className="text-xs font-medium text-gray-500">Instrument</label>
                        <p className="text-sm md:text-lg font-semibold text-gray-900">{formatNumber(flight.actual_instrument)}h</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Crew */}
                <div className="bg-purple-50 rounded-xl p-4 md:p-6">
                  <h3 className="text-base md:text-lg font-bold text-gray-900 mb-3 md:mb-4 flex items-center">
                    <div className="p-1.5 bg-purple-200 rounded-lg mr-2 md:mr-3">
                      <Users className="h-3 w-3 md:h-4 md:w-4 text-purple-600" />
                    </div>
                    Crew
                  </h3>
                  <div className="space-y-3">
                    <div className="bg-white rounded-lg p-3 md:p-4">
                      <label className="text-xs font-semibold text-purple-600 uppercase tracking-wide">Pilot in Command</label>
                      <p className="text-gray-900 font-semibold mt-1 text-sm">{flight.pic_crew_name || 'N/A'}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 md:p-4">
                      <label className="text-xs font-semibold text-purple-600 uppercase tracking-wide">Second in Command</label>
                      <p className="text-gray-900 font-semibold mt-1 text-sm">{flight.sic_crew_name || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Remarks */}
            {flight.remarks && (
              <div className="mt-6 md:mt-8">
                <div className="bg-amber-50 rounded-xl p-4 md:p-6">
                  <h3 className="text-base md:text-lg font-bold text-gray-900 mb-3 md:mb-4 flex items-center">
                    <div className="p-1.5 bg-amber-200 rounded-lg mr-2 md:mr-3">
                      <AlertTriangle className="h-3 w-3 md:h-4 md:w-4 text-amber-600" />
                    </div>
                    Remarks
                  </h3>
                  <div className="bg-white rounded-lg p-3 md:p-4">
                    <p className="text-gray-900 leading-relaxed text-sm">{flight.remarks}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={`${isMobile ? 'px-4 py-3' : 'px-8 py-4'} border-t border-gray-200 bg-gray-50`}>
          <div className={`flex ${isMobile ? 'flex-col space-y-3' : 'items-center justify-between'}`}>
            <div className="text-xs md:text-sm text-gray-500">
              <span className="font-medium">Created:</span> {format(new Date(flight.created_at || Date.now()), 'MMM dd, yyyy HH:mm')}
            </div>
            <div className={`flex ${isMobile ? 'space-x-2' : 'items-center space-x-3'}`}>
              <button
                onClick={onClose}
                className={`${isMobile ? 'flex-1 px-4 py-2.5 text-sm' : 'px-6 py-2.5'} text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium`}
              >
                Close
              </button>
              <button
                onClick={() => onEdit(flight.id)}
                className={`${isMobile ? 'flex-1 px-4 py-2.5 text-sm' : 'px-6 py-2.5'} text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center font-medium`}
              >
                <Edit className="h-4 w-4 mr-1 md:mr-2" />
                Edit
              </button>
              <button
                onClick={() => onDelete(flight.id)}
                className={`${isMobile ? 'flex-1 px-4 py-2.5 text-sm' : 'px-6 py-2.5'} text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center font-medium`}
              >
                <Trash2 className="h-4 w-4 mr-1 md:mr-2" />
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FlightLog = () => {
  const { confirm } = useDialog();
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('individual');
  const [expandedTrips, setExpandedTrips] = useState(new Set());
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 250,
    total: 0,
    totalPages: 0,
    hasMore: false,
    isLimitedLoad: false,
    allLoaded: false
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    dateFrom: '',
    dateTo: '',
    aircraft: '',
    departure: '',
    arrival: '',
    crew: '',
    flightNumber: '',
    status: '',
    trip: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);
  
  // Trip deletion state
  const [showTripDeleteModal, setShowTripDeleteModal] = useState(false);
  // Selecting several flights to delete at once (list view only)
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [selectedTripForDeletion, setSelectedTripForDeletion] = useState(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Ref for flight elements to enable auto-scrolling
  const flightRefs = useRef({});
  
  // Function to find flight closest to today's date
  const findClosestFlightToToday = (flightList) => {
    if (!flightList || flightList.length === 0) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let closestFlight = null;
    let minDifference = Infinity;
    
    flightList.forEach((flight, index) => {
      const flightDate = new Date(flight.date);
      flightDate.setHours(0, 0, 0, 0);
      
      const difference = Math.abs(today.getTime() - flightDate.getTime());
      
      if (difference < minDifference) {
        minDifference = difference;
        closestFlight = flight;
      }
    });
    
    return closestFlight;
  };
  
  // Auto-scroll to flight closest to today when flights are loaded
  useEffect(() => {
    if (flights.length > 0 && !searchQuery && !Object.values(filters).some(v => v)) {
      const timer = setTimeout(() => {
        const closestFlight = findClosestFlightToToday(flights);
        if (closestFlight && flightRefs.current[closestFlight.id]) {
          const flightElement = flightRefs.current[closestFlight.id];
          
          // Use scrollIntoView for more reliable positioning
          flightElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center', // Center the element vertically in the viewport
            inline: 'nearest'
          });
        } else if (closestFlight) {
          console.warn('Closest flight found but ref not available:', closestFlight.date);
        }
      }, 500); // Slightly longer delay to ensure all UI elements are rendered
      
      return () => clearTimeout(timer);
    }
  }, [flights, searchQuery, filters, isMobile]);

  useEffect(() => {
    const tripParam = searchParams.get('trip');
    if (tripParam) {
      setFilters(prev => ({ ...prev, trip: tripParam }));
    }
  }, [searchParams]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initial load only. fetchFlights reads the current filters/sort, so listing
  // it as a dependency would refetch on every change.
  useEffect(() => {
    fetchFlights(1, false, true); // Use fast load for initial page load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      if (isSearching) {
        setIsSearching(false);
        fetchFlights(1);
      }
      return;
    }
    const timer = setTimeout(() => {
      setIsSearching(true);
      handleSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
    // Run only when the search text changes (debounced), not on every re-render
    // of the fetch/search helpers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const fetchFlights = async (page = 1, append = false, fastLoad = false) => {
    try {
      if (page === 1) setLoading(true);
      const authHeaders = getAuthHeaders(navigate);
      if (!authHeaders) return;
      
      // Use fast load endpoint for initial page load or when explicitly requested
      const endpoint = fastLoad || (page === 1 && !searchQuery.trim() && !Object.values(filters).some(v => v)) 
        ? '/api/flights/recent-limited' 
        : '/api/flights/recent';
      
      const params = new URLSearchParams();
      
      if (endpoint === '/api/flights/recent-limited') {
        // For fast load, just get the most recent 25 flights
        params.append('limit', '100');
      } else {
        params.append('limit', pagination.limit.toString());
        params.append('page', page.toString());
        params.append('sortBy', sortField);
        params.append('sortOrder', sortDirection);
        
        Object.entries(filters).forEach(([key, value]) => {
          if (value) params.append(key, value);
        });

        if (searchQuery.trim()) {
          params.append('search', searchQuery.trim());
        }
      }

      const response = await axios.get(`${endpoint}?${params}`, authHeaders);
      
      if (append) {
        setFlights(prev => [...prev, ...response.data.flights]);
      } else {
        setFlights(response.data.flights);
      }
      
      setPagination(prev => ({
        ...prev,
        page: page,
        total: response.data.total,
        totalPages: Math.ceil(response.data.total / (response.data.limit || prev.limit)),
        hasMore: response.data.hasMore,
        isLimitedLoad: response.data.isLimitedLoad || false,
        allLoaded: response.data.allLoaded || false
      }));
    } catch (err) {
      if (!handleAuthError(err, navigate)) {
        setError('Failed to fetch flight data. Please try again.');
        toast.error('Failed to fetch flights');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    try {
      // Use isSearching, NOT setLoading — setLoading(true) replaces the whole
      // page with a spinner which unmounts the input and loses focus.
      setIsSearching(true);
      const authHeaders = getAuthHeaders(navigate);
      if (!authHeaders) return;

      const params = new URLSearchParams({
        q: query,          // route expects 'q', not 'search'
        limit: pagination.limit.toString()
      });

      const response = await axios.get(`/api/flights/search?${params}`, authHeaders);
      setFlights(response.data.flights);
      setPagination(prev => ({
        ...prev,
        page: 1,
        total: response.data.total,
        totalPages: Math.ceil(response.data.total / prev.limit),
        hasMore: false
      }));
    } catch (err) {
      if (!handleAuthError(err, navigate)) {
        toast.error('Search failed');
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    fetchFlights(1);
  };

  const handleView = async (flightId) => {
    try {
      const authHeaders = getAuthHeaders(navigate);
      if (!authHeaders) return;
      
      const response = await axios.get(`/api/flights/${flightId}`, authHeaders);
      setSelectedFlight(response.data);
      setShowViewModal(true);
    } catch (err) {
      if (!handleAuthError(err, navigate)) {
        toast.error('Failed to load flight details.');
      }
    }
  };

  const handleEdit = (flightId) => {
    navigate(`/edit-flight/${flightId}`);
    setShowViewModal(false);
  };

  // New flight pre-filled as the reverse of this one
  const handleReturnLeg = (flightId) => {
    navigate(`/add-flight?returnOf=${flightId}`);
  };

  const handleDelete = async (flightId) => {
    if (!(await confirm({ title: 'Delete this flight?', message: 'This can\'t be undone.', confirmLabel: 'Delete flight', tone: 'danger' }))) {
      return;
    }

    try {
      const authHeaders = getAuthHeaders(navigate);
      if (!authHeaders) return;

      await axios.delete(`/api/flights/${flightId}`, authHeaders);
      toast.success('Flight deleted successfully');
      setShowViewModal(false);
      fetchFlights(pagination.page);
    } catch (err) {
      if (!handleAuthError(err, navigate)) {
        toast.error('Failed to delete flight');
      }
    }
  };

  // Trip deletion handlers
  const handleTripDelete = (tripId, tripNumber) => {
    setSelectedTripForDeletion({ id: tripId, number: tripNumber });
    setShowTripDeleteModal(true);
  };

  const handleTripDeleted = () => {
    // Refresh the flight data after trip deletion
    fetchFlights(1, false, true); // Force refresh
    setSelectedTripForDeletion(null);
  };

  // Multi-select delete
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const toggleSelected = (flightId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(flightId)) next.delete(flightId);
      else next.add(flightId);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    const ids = selectedVisibleIds;
    if (ids.length === 0) return;
    const noun = `${ids.length} flight${ids.length !== 1 ? 's' : ''}`;
    if (!(await confirm({ title: `Delete ${noun}?`, message: 'This can\'t be undone.', confirmLabel: `Delete ${noun}`, tone: 'danger' }))) {
      return;
    }

    try {
      const authHeaders = getAuthHeaders(navigate);
      if (!authHeaders) return;

      setBulkDeleting(true);
      const response = await axios.post('/api/flights/bulk-delete', { ids }, authHeaders);
      const deleted = response.data.deleted ?? ids.length;
      toast.success(`Deleted ${deleted} flight${deleted !== 1 ? 's' : ''}`);
      exitSelectMode();
      fetchFlights(1);
    } catch (err) {
      if (!handleAuthError(err, navigate)) {
        toast.error(err.response?.data?.error || 'Failed to delete flights');
      }
    } finally {
      setBulkDeleting(false);
    }
  };

  const loadAllFlights = async () => {
    try {
      setLoading(true);
      const authHeaders = getAuthHeaders(navigate);
      if (!authHeaders) return;
      
      const response = await axios.get('/api/flights/all', authHeaders);
      setFlights(response.data.flights);
      setPagination(prev => ({
        ...prev,
        page: 1,
        total: response.data.total,
        totalPages: 1,
        hasMore: false,
        isLimitedLoad: false,
        allLoaded: true
      }));
      toast.success(`All ${response.data.flights.length} flights loaded`);
    } catch (err) {
      if (!handleAuthError(err, navigate)) {
        toast.error('Failed to load all flights');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMoreFlights = async () => {
    try {
      setLoading(true);
      const authHeaders = getAuthHeaders(navigate);
      if (!authHeaders) return;
      
      // Calculate next batch - load current count + 250 more
      const newLimit = flights.length + 250;
      const response = await axios.get(`/api/flights/recent?limit=${newLimit}&page=1`, authHeaders);
      setFlights(response.data.flights);
      setPagination(prev => ({
        ...prev,
        page: 1,
        total: response.data.total,
        totalPages: Math.ceil(response.data.total / response.data.limit),
        hasMore: response.data.hasMore,
        isLimitedLoad: response.data.total > response.data.flights.length
      }));
      toast.success(`Loaded ${response.data.flights.length} flights`);
    } catch (err) {
      if (!handleAuthError(err, navigate)) {
        toast.error('Failed to load more flights');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleTripExpansion = (tripId) => {
    const newExpanded = new Set(expandedTrips);
    if (newExpanded.has(tripId)) {
      newExpanded.delete(tripId);
    } else {
      newExpanded.add(tripId);
    }
    setExpandedTrips(newExpanded);
  };

  const groupedFlights = useMemo(() => {
    const groups = {};
    
    flights.forEach(flight => {
      const tripId = flight.trip_id || 'no-trip';
      const tripNumber = flight.trip_number || flight.trip_display || null;
      
      if (!groups[tripId]) {
        groups[tripId] = {
          tripId,
          tripNumber: tripNumber || 'Individual Flights',
          flights: [],
          totalTime: 0,
          totalPicTime: 0,
          totalSicTime: 0,
          firstDate: flight.date,
          lastDate: flight.date,
          hasTrip: !!flight.trip_id,
          tripOnDuty: flight.trip_on_duty,
          tripOffDuty: flight.trip_off_duty,
          tafbDays: flight.trip_tafb_days,
          tafbHours: flight.trip_tafb_hours
        };
      }
      
      groups[tripId].flights.push(flight);
      
      const totalTime = parseFloat(flight.total_time) || 0;
      const picTime = parseFloat(flight.pic_time) || 0;
      const sicTime = parseFloat(flight.sic_time) || 0;
      
      groups[tripId].totalTime += totalTime;
      groups[tripId].totalPicTime += picTime;
      groups[tripId].totalSicTime += sicTime;
      
      if (flight.date < groups[tripId].firstDate) {
        groups[tripId].firstDate = flight.date;
      }
      if (flight.date > groups[tripId].lastDate) {
        groups[tripId].lastDate = flight.date;
      }
    });

    return Object.values(groups).sort((a, b) => {
      if (a.tripId === 'no-trip') return 1;
      if (b.tripId === 'no-trip') return -1;
      return new Date(b.firstDate) - new Date(a.firstDate);
    }).map(group => ({
      ...group,
      flights: group.flights.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        
        // First, sort by date
        const dateComparison = dateB - dateA; // Most recent date first
        
        // If dates are the same, sort by departure time (latest time first)
        if (dateComparison === 0) {
          const timeA = a.actual_out || a.scheduled_out || 0;
          const timeB = b.actual_out || b.scheduled_out || 0;
          
          // Convert time to comparable format (handle null/undefined)
          const getComparableTime = (time) => {
            if (!time) return 0;
            return parseInt(time.toString().padStart(4, '0'));
          };
          
          const comparableTimeA = getComparableTime(timeA);
          const comparableTimeB = getComparableTime(timeB);
          
          // Sort by departure time descending (latest time first) for same date
          return comparableTimeB - comparableTimeA;
        }
        
        return dateComparison;
      })
    }));
  }, [flights]);

  const filteredFlights = useMemo(() => {
    const filtered = flights.filter(flight => {
      const matchesSearch = !searchQuery || 
        flight.flight_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        flight.aircraft_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        flight.departure_airport?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        flight.arrival_airport?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        flight.trip_number?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesDateFrom = !filters.dateFrom || 
        new Date(flight.date) >= new Date(filters.dateFrom);

      const matchesDateTo = !filters.dateTo || 
        new Date(flight.date) <= new Date(filters.dateTo);

      const matchesAircraft = !filters.aircraft || 
        flight.aircraft_id?.toLowerCase().includes(filters.aircraft.toLowerCase());

      const matchesTrip = !filters.trip || 
        flight.trip_number?.toLowerCase().includes(filters.trip.toLowerCase());

      const flightStatus = getFlightStatus(flight);
      const matchesStatus = !filters.status || flightStatus.status === filters.status;

      return matchesSearch && matchesDateFrom && matchesDateTo && 
             matchesAircraft && matchesTrip && matchesStatus;
    });

    // Add secondary sorting for same-date flights
    // When sorting by date, add secondary sorting by departure time
    if (sortField === 'date') {
      return filtered.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        
        // First, sort by date
        const dateComparison = sortDirection === 'desc' ? dateB - dateA : dateA - dateB;
        
        // If dates are the same, sort by departure time (latest first for same date)
        if (dateComparison === 0) {
          const timeA = a.actual_out || a.scheduled_out || 0;
          const timeB = b.actual_out || b.scheduled_out || 0;
          
          // Convert time to comparable format (handle null/undefined)
          const getComparableTime = (time) => {
            if (!time) return 0;
            return parseInt(time.toString().padStart(4, '0'));
          };
          
          const comparableTimeA = getComparableTime(timeA);
          const comparableTimeB = getComparableTime(timeB);
          
          // Sort by departure time descending (latest time first) for same date
          return comparableTimeB - comparableTimeA;
        }
        
        return dateComparison;
      });
    }
    
    // For other sort fields, just return the filtered flights (backend sorting applies)
    return filtered;
  }, [flights, searchQuery, filters, sortField, sortDirection]);

  // Only flights still on screen count as selected, so a search or filter
  // change can't delete flights the pilot can no longer see
  const selectedVisibleIds = useMemo(
    () => filteredFlights.filter(f => selectedIds.has(f.id)).map(f => f.id),
    [filteredFlights, selectedIds]
  );
  const allVisibleSelected = filteredFlights.length > 0 && selectedVisibleIds.length === filteredFlights.length;
  const toggleSelectAllVisible = () => {
    setSelectedIds(allVisibleSelected ? new Set() : new Set(filteredFlights.map(f => f.id)));
  };

  // Selecting is only offered in the list view
  useEffect(() => {
    if (viewMode !== 'individual') exitSelectMode();
  }, [viewMode]);

  const clearFilters = () => {
    setFilters({
      search: '',
      dateFrom: '',
      dateTo: '',
      aircraft: '',
      departure: '',
      arrival: '',
      crew: '',
      flightNumber: '',
      status: '',
      trip: ''
    });
    setSearchQuery('');
    fetchFlights(1);
  };

  const renderTripGroup = (group) => {
    const isExpanded = expandedTrips.has(group.tripId);
    
    return (
      <div key={group.tripId} className="bg-white rounded-lg shadow border border-gray-200 mb-4">
        <div 
          className="p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50"
          onClick={() => toggleTripExpansion(group.tripId)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {isExpanded ? <ChevronDown className="h-5 w-5 text-gray-400" /> : <ChevronRight className="h-5 w-5 text-gray-400" />}
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  {group.hasTrip ? (
                    <>
                      <Layers className="h-5 w-5 mr-2 text-blue-600" />
                      Trip {group.tripNumber}
                    </>
                  ) : (
                    <>
                      <Plane className="h-5 w-5 mr-2 text-gray-600" />
                      {group.tripNumber}
                    </>
                  )}
                </h3>
                <div className="text-sm text-gray-500 mt-1">
                  {group.flights.length} flight{group.flights.length !== 1 ? 's' : ''} • {formatDuration(group.totalTime)} total time
                  {group.firstDate !== group.lastDate && (
                    <span> • {formatDate(group.firstDate)} to {formatDate(group.lastDate)}</span>
                  )}
                  {group.tafbDays && group.tafbHours && (
                    <span> • TAFB: {group.tafbDays}d ({formatNumber(group.tafbHours)}h)</span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="text-right text-sm">
                <div className="text-gray-900 font-medium">{formatDuration(group.totalTime)}</div>
                <div className="text-gray-500">
                  PIC: {formatDuration(group.totalPicTime)} | SIC: {formatDuration(group.totalSicTime)}
                </div>
              </div>

              {group.hasTrip && (
                <div className="flex items-center space-x-2">
                  <Link
                    to={`/flights?trip=${group.tripNumber}`}
                    className="text-blue-600 hover:text-blue-900 p-1"
                    title="View Trip Details"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Eye className="h-4 w-4" />
                  </Link>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTripDelete(group.tripId, group.tripNumber);
                    }}
                    className="p-1 text-red-600 hover:text-red-900"
                    title="Delete Trip"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-gray-500" />
                      Status
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      Date
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                    <div className="flex items-center gap-2">
                      <Plane className="h-4 w-4 text-gray-500" />
                      Flight #
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">Aircraft</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-gray-500" />
                      Route
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gray-500" />
                      Times
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">Total</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">PIC</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">SIC</th>
                  <th className="relative px-6 py-4"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {group.flights.map((flight, index) => {
                  const flightStatus = getFlightStatus(flight);
                  
                  // Enhanced row styling based on flight status for grouped view
                  const getRowStyling = () => {
                    const baseClasses = 'transition-all duration-200';
                    switch (flightStatus.status) {
                      case 'completed':
                        return `${baseClasses} hover:bg-green-50 bg-green-50/30`;
                      case 'planned':
                        return `${baseClasses} hover:bg-blue-50 bg-blue-50/30`;
                      case 'in-progress':
                        return `${baseClasses} hover:bg-yellow-50 bg-yellow-50/30 shadow-sm`;
                      case 'past':
                        return `${baseClasses} hover:bg-orange-50 bg-orange-50/30 opacity-90`;
                      default:
                        return `${baseClasses} hover:bg-gray-50`;
                    }
                  };

                  const getBorderColor = () => {
                    switch (flightStatus.status) {
                      case 'completed': return '#10b981';
                      case 'planned': return '#3b82f6';
                      case 'in-progress': return '#f59e0b';
                      case 'past': return '#f97316';
                      default: return 'transparent';
                    }
                  };
                  
                  return (
                    <tr 
                      key={flight.id || index} 
                      ref={(el) => {
                        if (el) {
                          flightRefs.current[flight.id] = el;
                        }
                      }}
                      className={getRowStyling()}
                      style={{
                        borderLeft: `4px solid ${getBorderColor()}`
                      }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${flightStatus.color}`}>
                          {flightStatus.status === 'in-progress' && <AlertTriangle className="h-3 w-3 mr-1" />}
                          {flightStatus.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatDate(flight.date)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{flight.flight_number || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div>
                          <div className="font-medium text-gray-900">{flight.aircraft_id || 'N/A'}</div>
                          <div className="text-xs text-gray-400">{getAircraftType(flight)}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="space-y-1">
                          <div className="flex items-center">
                            <PlaneTakeoff className="h-4 w-4 mr-1 text-gray-400" />
                            <span className="font-medium">{flight.departure_airport || 'N/A'}</span>
                          </div>
                          <div className="flex items-center">
                            <PlaneLanding className="h-4 w-4 mr-1 text-gray-400" />
                            <span className="font-medium">{flight.arrival_airport || 'N/A'}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="text-xs space-y-1">
                          <div>Out: {formatTime(flight.actual_out || flight.scheduled_out)}</div>
                          <div>Off: {formatTime(flight.actual_off)}</div>
                          <div>On: {formatTime(flight.actual_on)}</div>
                          <div>In: {formatTime(flight.actual_in || flight.scheduled_in)}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatNumber(flight.total_time)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatNumber(flight.pic_time)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatNumber(flight.sic_time)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button onClick={() => handleView(flight.id)} className="text-blue-600 hover:text-blue-900 mr-4">
                          <Eye className="h-5 w-5" />
                        </button>
                        <button onClick={() => handleEdit(flight.id)} className="text-indigo-600 hover:text-indigo-900 mr-4">
                          <Edit className="h-5 w-5" />
                        </button>
                        <button onClick={() => handleReturnLeg(flight.id)} className="text-sky-600 hover:text-sky-900 mr-4" title="Log return leg" aria-label="Log return leg">
                          <ArrowLeftRight className="h-5 w-5" />
                        </button>
                        <button onClick={() => handleDelete(flight.id)} className="text-red-600 hover:text-red-900">
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderIndividualFlights = () => (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
      <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 220px)' }}>
        <table className="min-w-full divide-y divide-gray-200 flight-table">
          <thead className="bg-gradient-to-r from-slate-50 via-slate-100 to-slate-50 sticky top-0 z-20 shadow-sm">
            <tr className="border-b-2 border-slate-200">
              {selectMode && (
                <th className="px-4 py-4 text-left border-r border-slate-200/60 bg-white/90 backdrop-blur-sm">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    aria-label="Select all flights shown"
                  />
                </th>
              )}
              <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wide border-r border-slate-200/60 bg-white/90 backdrop-blur-sm">
                <div className="flex items-center gap-2 min-w-max">
                  <AlertTriangle className="h-4 w-4 text-slate-500" />
                  <span>Status</span>
                </div>
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wide cursor-pointer hover:bg-slate-200/70 transition-colors border-r border-slate-200/60 bg-white/90 backdrop-blur-sm group" onClick={() => handleSort('date')}>
                <div className="flex items-center gap-2 min-w-max">
                  <Calendar className="h-4 w-4 text-slate-500 group-hover:text-blue-600 transition-colors" />
                  <span className="group-hover:text-slate-900">Date</span>
                  {sortField === 'date' && <span className="text-blue-600 font-black text-sm">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wide cursor-pointer hover:bg-slate-200/70 transition-colors border-r border-slate-200/60 bg-white/90 backdrop-blur-sm group" onClick={() => handleSort('flight_number')}>
                <div className="flex items-center gap-2 min-w-max">
                  <Plane className="h-4 w-4 text-slate-500 group-hover:text-blue-600 transition-colors" />
                  <span className="group-hover:text-slate-900">Flight #</span>
                  {sortField === 'flight_number' && <span className="text-blue-600 font-black text-sm">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wide border-r border-slate-200/60 bg-white/90 backdrop-blur-sm">
                <div className="flex items-center gap-2 min-w-max">
                  <Layers className="h-4 w-4 text-slate-500" />
                  <span>Trip</span>
                </div>
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wide cursor-pointer hover:bg-slate-200/70 transition-colors border-r border-slate-200/60 bg-white/90 backdrop-blur-sm group" onClick={() => handleSort('aircraft_id')}>
                <div className="flex items-center gap-2 min-w-max">
                  <span className="group-hover:text-slate-900">Aircraft</span>
                  {sortField === 'aircraft_id' && <span className="text-blue-600 font-black text-sm">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wide border-r border-slate-200/60 bg-white/90 backdrop-blur-sm">
                <div className="flex items-center gap-2 min-w-max">
                  <MapPin className="h-4 w-4 text-slate-500" />
                  <span>Route</span>
                </div>
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wide border-r border-slate-200/60 bg-white/90 backdrop-blur-sm">
                <div className="flex items-center gap-2 min-w-max">
                  <Clock className="h-4 w-4 text-slate-500" />
                  <span>Times</span>
                </div>
              </th>
              <th className="px-6 py-4 text-center text-xs font-bold text-slate-700 uppercase tracking-wide cursor-pointer hover:bg-slate-200/70 transition-colors border-r border-slate-200/60 bg-white/90 backdrop-blur-sm group" onClick={() => handleSort('total_time')}>
                <div className="flex items-center justify-center gap-2 min-w-max">
                  <span className="group-hover:text-slate-900">Total</span>
                  {sortField === 'total_time' && <span className="text-blue-600 font-black text-sm">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
              </th>
              <th className="px-6 py-4 text-center text-xs font-bold text-slate-700 uppercase tracking-wide cursor-pointer hover:bg-slate-200/70 transition-colors border-r border-slate-200/60 bg-white/90 backdrop-blur-sm group" onClick={() => handleSort('pic_time')}>
                <div className="flex items-center justify-center gap-2 min-w-max">
                  <span className="group-hover:text-slate-900">PIC</span>
                  {sortField === 'pic_time' && <span className="text-blue-600 font-black text-sm">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
              </th>
              <th className="px-6 py-4 text-center text-xs font-bold text-slate-700 uppercase tracking-wide cursor-pointer hover:bg-slate-200/70 transition-colors border-r border-slate-200/60 bg-white/90 backdrop-blur-sm group" onClick={() => handleSort('sic_time')}>
                <div className="flex items-center justify-center gap-2 min-w-max">
                  <span className="group-hover:text-slate-900">SIC</span>
                  {sortField === 'sic_time' && <span className="text-blue-600 font-black text-sm">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wide border-r border-slate-200/60 bg-white/90 backdrop-blur-sm">
                <div className="flex items-center gap-2 min-w-max">
                  <User className="h-4 w-4 text-slate-500" />
                  <span>Crew</span>
                </div>
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wide border-r border-slate-200/60 bg-white/90 backdrop-blur-sm">
                <span>Remarks</span>
              </th>
              <th className="relative px-6 py-4 bg-white/90 backdrop-blur-sm">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {filteredFlights.map((flight, index) => {
            const flightStatus = getFlightStatus(flight);
            
            // Enhanced row styling based on flight status
            const getRowStyling = () => {
              const baseClasses = 'transition-all duration-200 border-b border-gray-100 hover:shadow-sm';
              switch (flightStatus.status) {
                case 'completed':
                  return `${baseClasses} hover:bg-green-50 bg-green-50/30`;
                case 'planned':
                  return `${baseClasses} hover:bg-blue-50 bg-blue-50/30`;
                case 'in-progress':
                  return `${baseClasses} hover:bg-yellow-50 bg-yellow-50/30 shadow-sm hover:shadow-md`;
                case 'past':
                  return `${baseClasses} hover:bg-orange-50 bg-orange-50/30 opacity-90`;
                default:
                  return `${baseClasses} hover:bg-slate-50`;
              }
            };

            const getBorderColor = () => {
              switch (flightStatus.status) {
                case 'completed': return '#10b981';
                case 'planned': return '#3b82f6';
                case 'in-progress': return '#f59e0b';
                case 'past': return '#f97316';
                default: return 'transparent';
              }
            };
            
            const getBorderClass = () => {
              switch (flightStatus.status) {
                case 'completed': return 'flight-border-completed';
                case 'planned': return 'flight-border-planned';
                case 'in-progress': return 'flight-border-progress';
                case 'past': return 'flight-border-past';
                default: return '';
              }
            };

            return (
              <tr 
                key={flight.id || index} 
                ref={(el) => {
                  if (el) {
                    flightRefs.current[flight.id] = el;
                  }
                }}
                className={`${getRowStyling()} ${getBorderClass()}`}
                style={{
                  borderLeft: `4px solid ${getBorderColor()}`
                }}
              >
                {selectMode && (
                  <td className="px-4 py-4">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(flight.id)}
                      onChange={() => toggleSelected(flight.id)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      aria-label={`Select flight ${flight.flight_number || ''}`}
                    />
                  </td>
                )}
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${flightStatus.color} shadow-sm`}>
                    {flightStatus.status === 'in-progress' && <AlertTriangle className="h-3 w-3 mr-1.5" />}
                    {flightStatus.label}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">{formatDate(flight.date)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-700">{flight.flight_number || 'N/A'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {flight.trip_number || flight.trip_display ? (
                    <Link
                      to={`/flights?trip=${encodeURIComponent(flight.trip_number || flight.trip_display)}`}
                      className="text-blue-600 hover:text-blue-800 flex items-center font-medium transition-colors"
                      title="View Trip Flights"
                    >
                      <Layers className="h-4 w-4 mr-1.5" />
                      {flight.trip_number || flight.trip_display}
                    </Link>
                  ) : (
                    <span className="text-gray-400 flex items-center">
                      <Plane className="h-4 w-4 mr-1.5" />
                      Individual
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  <div className="space-y-0.5">
                    <div className="font-semibold text-gray-900">{flight.aircraft_id || 'N/A'}</div>
                    <div className="text-xs text-gray-500 font-medium">{getAircraftType(flight)}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  <div className="space-y-1.5">
                    <div className="flex items-center">
                      <PlaneTakeoff className="h-4 w-4 mr-1.5 text-gray-400" />
                      <span className="font-semibold text-gray-900">{flight.departure_airport || 'N/A'}</span>
                    </div>
                    <div className="flex items-center">
                      <PlaneLanding className="h-4 w-4 mr-1.5 text-gray-400" />
                      <span className="font-semibold text-gray-900">{flight.arrival_airport || 'N/A'}</span>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  <div className="text-xs space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-500 font-medium">Out:</span>
                      <span className="font-mono font-semibold text-gray-900">{formatTime(flight.actual_out || flight.scheduled_out)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 font-medium">Off:</span>
                      <span className="font-mono font-semibold text-gray-900">{formatTime(flight.actual_off)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 font-medium">On:</span>
                      <span className="font-mono font-semibold text-gray-900">{formatTime(flight.actual_on)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 font-medium">In:</span>
                      <span className="font-mono font-semibold text-gray-900">{formatTime(flight.actual_in || flight.scheduled_in)}</span>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center text-base font-bold text-gray-900">
                  {formatNumber(flight.total_time)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-semibold text-gray-700">{formatNumber(flight.pic_time)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-semibold text-gray-700">{formatNumber(flight.sic_time)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  <div className="text-xs space-y-1">
                    {flight.pic_crew_name && <div className="flex items-center"><span className="text-gray-500 font-medium mr-1">PIC:</span><span className="font-semibold">{flight.pic_crew_name}</span></div>}
                    {flight.sic_crew_name && <div className="flex items-center"><span className="text-gray-500 font-medium mr-1">SIC:</span><span className="font-semibold">{flight.sic_crew_name}</span></div>}
                    {!flight.pic_crew_name && !flight.sic_crew_name && <div className="text-gray-400 italic">N/A</div>}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={flight.remarks}>
                  <span className={flight.remarks ? "text-gray-900" : "text-gray-400 italic"}>
                    {flight.remarks || 'N/A'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-2">
                    <button 
                      onClick={() => handleView(flight.id)} 
                      className="text-blue-600 hover:text-blue-800 transition-colors p-1.5 rounded-md hover:bg-blue-50"
                      title="View Details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => handleEdit(flight.id)} 
                      className="text-indigo-600 hover:text-indigo-800 transition-colors p-1.5 rounded-md hover:bg-indigo-50"
                      title="Edit Flight"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => handleReturnLeg(flight.id)} 
                      className="text-sky-600 hover:text-sky-800 transition-colors p-1.5 rounded-md hover:bg-sky-50"
                      title="Log Return Leg"
                    >
                      <ArrowLeftRight className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => handleDelete(flight.id)} 
                      className="text-red-600 hover:text-red-800 transition-colors p-1.5 rounded-md hover:bg-red-50"
                      title="Delete Flight"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    </div>
  );

  const renderContent = () => {
    if (isMobile) {
      return (
        <div className="p-4">
          {viewMode === 'grouped' ? (
            <div className="space-y-4">
              {groupedFlights.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Plane className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No flights found</p>
                </div>
              ) : (
                groupedFlights.map(group => renderMobileTripGroup(group))
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredFlights.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Plane className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No flights found</p>
                </div>
              ) : (
                filteredFlights.map((flight, index) => (
                  <MobileFlightCard
                    key={flight.id || index}
                    ref={(el) => {
                      if (el) {
                        flightRefs.current[flight.id] = el;
                      }
                    }}
                    flight={flight}
                    onView={handleView}
                    onEdit={handleEdit}
                    onReturnLeg={handleReturnLeg}
                    onDelete={handleDelete}
                    selectable={selectMode}
                    selected={selectedIds.has(flight.id)}
                    onToggleSelect={toggleSelected}
                  />
                ))
              )}
            </div>
          )}
        </div>
      );
    } else {
      return (
        <div className="p-0"> {/* Removed p-6 to allow for full width content inside this container */}
          {viewMode === 'grouped' ? (
            <div className="space-y-4 px-6 py-6"> {/* Added padding back to content inside for grouped view */}
              {groupedFlights.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Plane className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No flights found matching your criteria</p>
                </div>
              ) : (
                groupedFlights.map(renderTripGroup)
              )}
            </div>
          ) : (
            <div>
              {renderIndividualFlights()}
              {filteredFlights.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Plane className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No flights found matching your criteria</p>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
  };

  const renderMobileTripGroup = (group) => {
    const isExpanded = expandedTrips.has(group.tripId);
    
    return (
      <div key={group.tripId} className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
        <div 
          className="p-4 border-b border-gray-200"
          onClick={() => toggleTripExpansion(group.tripId)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {isExpanded ? <ChevronDown className="h-5 w-5 text-gray-400" /> : <ChevronRight className="h-5 w-5 text-gray-400" />}
              <div>
                <h3 className="font-medium text-gray-900 flex items-center">
                  {group.hasTrip ? (
                    <>
                      <Layers className="h-4 w-4 mr-2 text-blue-600" />
                      Trip {group.tripNumber}
                    </>
                  ) : (
                    <>
                      <Plane className="h-4 w-4 mr-2 text-gray-600" />
                      {group.tripNumber}
                    </>
                  )}
                </h3>
                <div className="text-sm text-gray-500 mt-1">
                  {group.flights.length} flight{group.flights.length !== 1 ? 's' : ''} • {formatDuration(group.totalTime)}
                </div>
              </div>
            </div>
            
            <div className="text-right">
              <div className="text-lg font-semibold text-gray-900">{formatDuration(group.totalTime)}</div>
              <div className="text-xs text-gray-500">
                {group.firstDate !== group.lastDate ? 
                  `${formatDateMobile(group.firstDate)} - ${formatDateMobile(group.lastDate)}` : 
                  formatDateMobile(group.firstDate)
                }
              </div>
              {group.hasTrip && (
                <div className="flex items-center justify-end space-x-2 mt-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTripDelete(group.tripId, group.tripNumber);
                    }}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete Trip"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className="p-4 space-y-3">
            {group.flights.map((flight, index) => (
              <MobileFlightCard
                key={flight.id || index}
                ref={(el) => {
                  if (el) {
                    flightRefs.current[flight.id] = el;
                  }
                }}
                flight={flight}
                onView={handleView}
                onEdit={handleEdit}
                onReturnLeg={handleReturnLeg}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Flights</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => fetchFlights(1)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 ${selectMode ? 'pb-24' : ''}`}>
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isMobile ? (
            <div className="py-4">
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl font-bold text-gray-900">Flight Log</h1>
                <Link
                  to="/add-flight"
                  className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700"
                >
                  <Plus className="h-5 w-5" />
                </Link>
              </div>

              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search flights..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {isSearching ? (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-sky-600" />
                  </div>
                ) : searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>

              <div className="flex items-center justify-between mb-3">
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('individual')}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      viewMode === 'individual'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500'
                    }`}
                  >
                    <Plane className="h-4 w-4 mr-1 inline" />
                    List
                  </button>
                  <button
                    onClick={() => setViewMode('grouped')}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      viewMode === 'grouped'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500'
                    }`}
                  >
                    <Layers className="h-4 w-4 mr-1 inline" />
                    Trips
                  </button>
                </div>

                <div className="flex items-center gap-2">
                {viewMode === 'individual' && (
                  <button
                    onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
                    className={`flex items-center px-3 py-2 border rounded-lg ${
                      selectMode ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <CheckSquare className="h-4 w-4 mr-2" />
                    <span className="text-sm">{selectMode ? 'Done' : 'Select'}</span>
                  </button>
                )}
                <button
                  onClick={() => setShowFilters(true)}
                  className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  <span className="text-sm">Filter</span>
                </button>
                </div>
              </div>

              <div className="text-sm text-gray-600 text-center">
                Showing {viewMode === 'grouped' ? groupedFlights.length : filteredFlights.length} 
                {viewMode === 'grouped' ? ' trip groups' : ' flights'} of {pagination.total} total
                {filters.trip && <span className="text-blue-600"> • Trip: {filters.trip}</span>}
              </div>

              {/* Mobile Load More Controls */}
              {!pagination.allLoaded && pagination.total > flights.length && (
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-blue-700">
                      <div className="font-medium">{flights.length} of {pagination.total} flights loaded</div>
                      <div className="text-xs text-blue-600 mt-1">Load more to see all your flights</div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={loadMoreFlights}
                        className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                        disabled={loading}
                      >
                        <Plus className="h-4 w-4" />
                        +250
                      </button>
                      <button
                        onClick={loadAllFlights}
                        className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                        disabled={loading}
                      >
                        <Database className="h-4 w-4" />
                        All
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {pagination.allLoaded && (
                <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center justify-center gap-2 text-sm text-green-700">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="font-medium">All {pagination.total} flights loaded</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex justify-between items-center py-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Flight Log</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Track and manage your flight records
                  {filters.trip && <span className="ml-2 text-blue-600">• Filtered by trip: {filters.trip}</span>}
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('individual')}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      viewMode === 'individual'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Plane className="h-4 w-4 mr-2 inline" />
                    Individual
                  </button>
                  <button
                    onClick={() => setViewMode('grouped')}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      viewMode === 'grouped'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Layers className="h-4 w-4 mr-2 inline" />
                    By Trip
                  </button>
                </div>

                {viewMode === 'individual' && (
                  <button
                    onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
                    className={`px-4 py-2 rounded-lg border flex items-center ${
                      selectMode
                        ? 'bg-blue-50 text-blue-700 border-blue-300'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                    title="Select flights to delete"
                  >
                    <CheckSquare className="h-5 w-5 mr-2" />
                    {selectMode ? 'Done' : 'Select'}
                  </button>
                )}
                <button
                  onClick={() => fetchFlights(1)}
                  className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center"
                  title="Refresh"
                >
                  <RefreshCw className="h-5 w-5 mr-2" />
                  Refresh
                </button>
                <Link
                  to="/import"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                >
                  <Database className="h-5 w-5 mr-2" />
                  Import
                </Link>
                <Link
                  to="/add-flight"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  New Flight
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {!isMobile && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          {/* Compact Header Controls */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
            {/* Single Row Layout for Better Space Efficiency */}
            <div className="flex items-center justify-between gap-4">
              {/* Left: Search */}
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search flights..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 pr-8 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  {isSearching ? (
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-sky-600" />
                    </div>
                  ) : searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Center: Load Controls - Only when needed */}
              {!pagination.allLoaded && pagination.total > flights.length && (
                <div className="flex items-center gap-2 bg-blue-50 px-3 py-2 rounded-md border border-blue-200">
                  <div className="text-xs text-blue-700 font-medium">
                    {flights.length} of {pagination.total}
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={loadMoreFlights}
                      className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 transition-colors"
                      disabled={loading}
                    >
                      <Plus className="h-3 w-3" />
                      +250
                    </button>
                    <button
                      onClick={loadAllFlights}
                      className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 transition-colors"
                      disabled={loading}
                    >
                      <Database className="h-3 w-3" />
                      All
                    </button>
                  </div>
                </div>
              )}

              {/* Success State - Compact */}
              {pagination.allLoaded && (
                <div className="flex items-center gap-2 bg-green-50 px-3 py-2 rounded-md border border-green-200">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                  <span className="text-xs text-green-700 font-medium">
                    All {pagination.total} loaded
                  </span>
                </div>
              )}

              {/* Right: Stats & Controls */}
              <div className="flex items-center gap-3">
                <div className="text-xs text-gray-600">
                  Showing {viewMode === 'grouped' ? groupedFlights.length : filteredFlights.length} 
                  {viewMode === 'grouped' ? ' trips' : ' flights'}
                </div>
                
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-xs font-medium"
                >
                  <Filter className="h-3 w-3" />
                  Filters
                  {showFilters ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                </button>
                
                {(Object.values(filters).some(v => v) || searchQuery) && (
                  <button
                    onClick={clearFilters}
                    className="px-3 py-2 text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition-colors text-xs font-medium"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Collapsible Filters - Only when open */}
            {showFilters && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="grid grid-cols-5 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">From</label>
                    <input
                      type="date"
                      value={filters.dateFrom}
                      onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">To</label>
                    <input
                      type="date"
                      value={filters.dateTo}
                      onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Aircraft</label>
                    <input
                      type="text"
                      placeholder="Aircraft ID"
                      value={filters.aircraft}
                      onChange={(e) => setFilters(prev => ({ ...prev, aircraft: e.target.value }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Trip</label>
                    <input
                      type="text"
                      placeholder="Trip Number"
                      value={filters.trip}
                      onChange={(e) => setFilters(prev => ({ ...prev, trip: e.target.value }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={filters.status}
                      onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">All</option>
                      <option value="planned">Planned</option>
                      <option value="in-progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MODIFIED: Removed desktop max-width and horizontal padding (mx-auto px-4 sm:px-6 lg:px-8) */}
      <div className={`${isMobile ? '' : ''}`}>
        <div className="bg-white shadow">
          {renderContent()}
        </div>
      </div>

      {isMobile && !selectMode && (
        <div className="fixed bottom-4 right-4 flex flex-col space-y-3">
          <button
            onClick={() => fetchFlights(1, false, true)}
            className="bg-white text-gray-700 border border-gray-300 p-3 rounded-full shadow-lg hover:bg-gray-50"
            title="Refresh"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
          <Link
            to="/data-import"
            className="bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700"
            title="Import"
          >
            <Database className="h-5 w-5" />
          </Link>
        </div>
      )}

      {isMobile && showFilters && (
        <MobileFilterSheet
          filters={filters}
          setFilters={setFilters}
          onClose={() => setShowFilters(false)}
          onClear={clearFilters}
        />
      )}

      <FlightDetailsModal
        flight={selectedFlight}
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {selectMode && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-gray-700">
              <span className="font-medium">{selectedVisibleIds.length}</span> of {filteredFlights.length} shown selected
              <button onClick={toggleSelectAllVisible} className="ml-3 text-blue-600 hover:text-blue-800 font-medium">
                {allVisibleSelected ? 'Clear' : 'Select all'}
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={exitSelectMode}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={selectedVisibleIds.length === 0 || bulkDeleting}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {bulkDeleting
                  ? 'Deleting...'
                  : `Delete ${selectedVisibleIds.length || ''} flight${selectedVisibleIds.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      <TripDeleteModal
        isOpen={showTripDeleteModal}
        onClose={() => setShowTripDeleteModal(false)}
        tripId={selectedTripForDeletion?.id}
        tripNumber={selectedTripForDeletion?.number}
        navigate={navigate}
        onTripDeleted={handleTripDeleted}
      />
    </div>
  );
};

export default FlightLog;